import React, { useState } from 'react';
import { User, Lock, Bell, Shield, Users, Eye, Trash2, ChevronRight, Moon, Globe, Camera, Save, X, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { cn } from '../utils';
import { interests, getInterestLabel } from '../utils/interests';
import { handleImageUpload } from '../utils/imageUtils';

const SettingItem = ({ icon: Icon, title, description, action, danger, onClick, children }) => (
  <div className="border-b border-gray-50 last:border-0">
    <div className="flex items-center justify-between p-6 hover:bg-gray-50 transition-all active:scale-[0.99] cursor-pointer group" onClick={onClick}>
      <div className="flex items-center gap-4">
        <div className={cn(
          "w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm",
          danger ? "bg-red-50 text-red-600" : "bg-white text-gray-600 border border-gray-100 group-hover:border-brand-indigo/20 group-hover:text-brand-indigo"
        )}>
          <Icon size={20} />
        </div>
        <div>
          <h3 className={cn("font-bold text-sm", danger ? "text-red-600" : "text-gray-900")}>{title}</h3>
          <p className="text-xs text-gray-500 font-medium">{description}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {action && <span className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em]">{action}</span>}
        <ChevronRight size={18} className={cn("text-gray-300 group-hover:text-gray-900 transition-colors", children && "rotate-90")} />
      </div>
    </div>
    {children && (
      <div className="px-6 pb-6 pt-2 bg-gray-50/30 animate-in slide-in-from-top-2 duration-200">
        {children}
      </div>
    )}
  </div>
);

const Settings = () => {
  const { user, updateUser } = useAuth();
  const fileInputRef = React.useRef(null);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    interests: user?.interests || []
  });

  const toggleInterest = (id) => {
    const current = [...formData.interests];
    if (current.includes(id)) {
      setFormData({ ...formData, interests: current.filter(i => i !== id) });
    } else {
      setFormData({ ...formData, interests: [...current, id] });
    }
  };
  const [isSaving, setIsSyncing] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsSyncing(true);
      try {
        const resizedImage = await handleImageUpload(file, { maxSizeMB: 2, maxWidth: 500, maxHeight: 500 });
        await updateUser({ avatar: resizedImage });
      } catch (err) {
        console.error('Avatar update failed', err);
        alert(err.message);
      } finally {
        setIsSyncing(false);
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async (field) => {
    setIsSyncing(true);
    try {
      await updateUser({ [field]: formData[field] });
      setEditingField(null);
    } catch (err) {
      console.error('Update failed', err);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileChange} 
        accept="image/*" 
        className="hidden" 
      />
      <div className="mb-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">Settings</h1>
          <p className="text-gray-500 font-medium">Manage your account, privacy, and community preferences.</p>
        </div>
        {user && (
          <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-2xl border border-gray-100">
            <div 
              className="w-10 h-10 rounded-xl bg-brand-indigo overflow-hidden shadow-lg shadow-brand-indigo/20 cursor-pointer group relative"
              onClick={triggerFileInput}
            >
              <img src={user.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.name}`} alt="User" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera size={14} className="text-white" />
              </div>
            </div>
            <div className="text-left">
              <p className="text-xs font-bold text-gray-900">{user.name}</p>
              <p className="text-xxs font-bold text-gray-400 uppercase tracking-[0.15em] leading-none mt-1">{user.role?.replace('_', ' ')}</p>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-8">
        {/* Account Settings */}
        <section>
          <h2 className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-4 px-2">Account & Security</h2>
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
            <SettingItem 
              icon={User} 
              title="Profile Name" 
              description={user?.name} 
              action={editingField === 'name' ? "" : "Edit"}
              onClick={() => setEditingField(editingField === 'name' ? null : 'name')}
            >
              {editingField === 'name' && (
                <div className="flex gap-2">
                  <input 
                    className="flex-1 px-4 py-2 bg-white border border-gray-200 rounded-xl outline-none focus:border-brand-indigo/30 transition-all font-medium text-sm"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    autoFocus
                  />
                  <button 
                    onClick={() => handleSave('name')}
                    disabled={isSaving}
                    className="p-2 bg-brand-indigo text-white rounded-xl hover:bg-brand-indigo/90 transition-all"
                  >
                    <Save size={18} />
                  </button>
                </div>
              )}
            </SettingItem>
            
            <SettingItem 
              icon={Camera} 
              title="Profile Picture" 
              description="Change your avatar" 
              action="Change"
              onClick={triggerFileInput}
            />

            <SettingItem 
              icon={Globe} 
              title="Personal Bio" 
              description={user?.bio || "Tell us about yourself"} 
              action={editingField === 'bio' ? "" : "Edit"}
              onClick={() => setEditingField(editingField === 'bio' ? null : 'bio')}
            >
              {editingField === 'bio' && (
                <div className="space-y-3">
                  <textarea 
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-2xl outline-none focus:border-brand-indigo/30 transition-all font-medium text-sm min-h-[100px] resize-none"
                    value={formData.bio}
                    onChange={(e) => setFormData({...formData, bio: e.target.value})}
                    placeholder="Write a short bio..."
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <button 
                      onClick={() => setEditingField(null)}
                      className="px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-[0.15em] hover:bg-gray-100 rounded-xl transition-all"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={() => handleSave('bio')}
                      disabled={isSaving}
                      className="px-4 py-2 bg-brand-indigo text-white rounded-xl text-xs font-bold uppercase tracking-[0.15em] hover:bg-brand-indigo/90 transition-all shadow-lg shadow-brand-indigo/10 flex items-center gap-2"
                    >
                      <Save size={14} />
                      Save Bio
                    </button>
                  </div>
                </div>
              )}
            </SettingItem>

            <SettingItem 
              icon={Zap} 
              title="Interests" 
              description={formData.interests.length > 0 ? formData.interests.map(id => getInterestLabel(id)).join(', ') : "Select topics you care about"} 
              action={editingField === 'interests' ? "" : "Manage"}
              onClick={() => setEditingField(editingField === 'interests' ? null : 'interests')}
            >
              {editingField === 'interests' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {interests.map((i) => (
                      <button
                        key={i.id}
                        type="button"
                        onClick={() => toggleInterest(i.id)}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all text-left",
                          formData.interests.includes(i.id) 
                            ? "border-brand-indigo bg-brand-indigo/5 text-brand-indigo" 
                            : "border-gray-50 bg-white text-gray-400 hover:border-gray-100"
                        )}
                      >
                        <span className="shrink-0">{i.icon}</span>
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] truncate">{i.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2">
                    <button 
                      onClick={() => {
                        setFormData({ ...formData, interests: user?.interests || [] });
                        setEditingField(null);
                      }}
                      className="px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-[0.15em] hover:bg-gray-100 rounded-xl transition-all"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={() => handleSave('interests')}
                      disabled={isSaving}
                      className="px-4 py-2 bg-brand-indigo text-white rounded-xl text-xs font-bold uppercase tracking-[0.15em] hover:bg-brand-indigo/90 transition-all shadow-lg shadow-brand-indigo/10 flex items-center gap-2"
                    >
                      <Save size={14} />
                      Save Interests
                    </button>
                  </div>
                </div>
              )}
            </SettingItem>

            <SettingItem 
              icon={Lock} 
              title="Password & Auth" 
              description="Change your password and manage 2FA settings" 
            />
          </div>
        </section>

        {/* Privacy Settings */}
        <section>
          <h2 className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-4 px-2">Privacy & Visibility</h2>
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
            <SettingItem 
              icon={Eye} 
              title="Profile Visibility" 
              description="Control who can see your activity and contributions" 
              action="Public"
            />
            <SettingItem 
              icon={Shield} 
              title="Data & Privacy" 
              description="Manage your data usage and download your archive" 
            />
            <SettingItem 
              icon={Globe} 
              title="Language & Region" 
              description="Set your preferred language and timezone" 
              action="English (US)"
            />
          </div>
        </section>

        {/* Community Management */}
        <section>
          <h2 className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-4 px-2">Community & Experience</h2>
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
            <SettingItem 
              icon={Users} 
              title="Community Memberships" 
              description="Manage the communities you've joined" 
            />
            <SettingItem 
              icon={Moon} 
              title="Appearance" 
              description="Customize your Chooziq theme and layout" 
              action="Light"
            />
          </div>
        </section>

        {/* Danger Zone */}
        <section>
          <h2 className="text-xs font-bold text-red-400 uppercase tracking-[0.15em] mb-4 px-2">Danger Zone</h2>
          <div className="bg-white rounded-3xl border border-red-50 overflow-hidden shadow-sm shadow-red-50">
            <SettingItem 
              icon={Trash2} 
              title="Deactivate Account" 
              description="Temporarily hide your profile and data" 
              danger
            />
            <SettingItem 
              icon={Trash2} 
              title="Delete Permanently" 
              description="Irreversibly remove all your data from Chooziq" 
              danger
            />
          </div>
        </section>
      </div>

      <div className="mt-12 pt-8 border-t border-gray-100 text-center">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em]">Chooziq v1.2.0</p>
      </div>
    </div>
  );
};

export default Settings;
